import React, { useState, useEffect, useMemo, useRef } from "react";
import Axios from "axios";
import Barcode from "react-barcode";
import { useReactToPrint } from "react-to-print";
import {
  BiPackage,
  BiSortAlt2,
  BiPlus,
  BiTrash,
  BiPrinter,
  BiRefresh,
  BiCheckCircle,
  BiLayer,
  BiSliderAlt,
  BiTransferAlt,
  BiChevronUp,
  BiChevronDown,
  BiEditAlt,
  BiSearch,
} from "react-icons/bi";
import { show_alerta } from "../../functions";
import Logo from "../../assets/Logo20.jpeg";

const URL_API1 = process.env.REACT_APP_URL_API1;
const URL_API2 = `${process.env.REACT_APP_URL_API2}/api/v1`;
const RAW_API3 = process.env.REACT_APP_URL_API3 || "";
// Si el servidor de desarrollo de React se inició antes de cambiar el .env y aún conserva localhost:3005,
// apuntamos directamente a https://sistemahidraulico.mx/api-externa
const URL_API3 =
  !RAW_API3 || RAW_API3.includes("localhost:3005")
    ? "https://sistemahidraulico.mx/api-externa"
    : RAW_API3;
const API3_KEY =
  "sm_ecommerce_x2ve9yFf0aiDxh1HelezpVeyRAcngGwgEg3ZnSZwhGg2SaZrd2gQiysiVo86R3LcUZFFxZDSMADepof1jMLSumIbiqBRcbjyhvA78haaxnLrrbOuU3zqCi0kQXJf1gSc";

const API3_HEADERS = {
  "x-api-key": API3_KEY,
};

const inferirMarcaDesdeDescripcion = (descr = "") => {
  const txt = String(descr || "").toUpperCase();
  if (!txt) return "";
  if (txt.includes("SEALMARKET") || txt.includes("SEALMAR") || /\bSEALM\b/.test(txt)) {
    return "SEALMARKET";
  }
  const marcasConocidas = [
    "CATERPILLAR",
    "HERCULES",
    "HALLITE",
    "KASTAS",
    "MERKEL",
    "PARKER",
    "GARLOCK",
    "DICHTOMATIK",
    "FREUDENBERG",
    "TRELLEBORG",
    "NATIONAL",
    "CORTECO",
    "SKF",
    "NOK",
  ];
  for (const m of marcasConocidas) {
    if (txt.includes(m)) return m;
  }
  if (/\bHE$/.test(txt.trim())) return "HERCULES";
  return "";
};

// Mapa de respaldo por si el catálogo de nombres de almacén tarda o no responde
const ALMACENES_FALLBACK = {
  "1": "Durango",
  "3": "Fresnillo",
  "4": "Torreón",
  "5": "Mazatlán",
  "6": "Zacatecas",
  "7": "Querétaro",
  "10": "Fresnillo",
};

const normalizarTexto = (txt) =>
  String(txt || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/**
 * Extrae la existencia numérica correspondiente al almacén seleccionado
 * a partir del objeto EXISTENCIA devuelto por /inventario/productos-etiquetas-caja
 * (ej. { Durango: 67, Mazatlán: 29, Zacatecas: 77, Querétaro: 47, Fresnillo: 42 })
 */
const obtenerExistenciaEnAlmacen = (existenciaObj, almacenId, mapaNombres) => {
  if (!existenciaObj || typeof existenciaObj !== "object") return 0;
  const nombreAlmacen =
    mapaNombres?.[String(almacenId).trim()] ||
    ALMACENES_FALLBACK[String(almacenId).trim()] ||
    "";
  if (!nombreAlmacen) return 0;

  const targetNorm = normalizarTexto(nombreAlmacen);
  for (const [key, val] of Object.entries(existenciaObj)) {
    if (normalizarTexto(key) === targetNorm) {
      const num = parseFloat(val);
      return Number.isNaN(num) ? 0 : num;
    }
  }
  return 0;
};

/**
 * Consulta el endpoint POST /inventario/productos-etiquetas-caja
 * enviando { linea: ["..."] } como arreglo no vacío
 */
const consultarProductosEtiquetasCajaPorLinea = async (linea) => {
  const lineaLimpia = String(linea || "").trim().toUpperCase();
  if (!lineaLimpia) return [];

  const payload = { linea: [lineaLimpia] };

  const res = await Axios.post(
    `${URL_API1}/inventario/productos-etiquetas-caja`,
    payload,
    { headers: { "Content-Type": "application/json" } }
  );
  return Array.isArray(res.data) ? res.data : res.data?.data || res.data?.body || [];
};

/**
 * Convierte una medida (ej. "30", "12.5", "1-3/4", "3/8") a número flotante
 * para garantizar un ordenamiento estrictamente numérico de menor a mayor.
 */
const parseMedidaNumerica = (val) => {
  if (val === null || val === undefined) return Number.POSITIVE_INFINITY;
  const str = String(val).trim();
  if (!str) return Number.POSITIVE_INFINITY;

  // Caso fracción mixta tipo "1-3/4" o "1 3/4"
  const mixtaMatch = str.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/);
  if (mixtaMatch) {
    const entero = parseFloat(mixtaMatch[1]);
    const num = parseFloat(mixtaMatch[2]);
    const den = parseFloat(mixtaMatch[3]);
    if (den !== 0) return entero + num / den;
  }

  // Caso fracción simple tipo "3/4"
  const fracMatch = str.match(/^(\d+)\/(\d+)$/);
  if (fracMatch) {
    const num = parseFloat(fracMatch[1]);
    const den = parseFloat(fracMatch[2]);
    if (den !== 0) return num / den;
  }

  // Limpiar caracteres no numéricos excepto punto y signo
  const limpio = str.replace(/[^0-9.-]/g, "");
  const parsed = parseFloat(limpio);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

/**
 * Construye la cadena de medida que se muestra debajo de la clave en la etiqueta:
 * Ej. "30x40x12.5"
 */
const construirMedidaEtiqueta = (prod, ordenDiametro) => {
  const di = String(prod.diametrointerior ?? "").trim();
  const de = String(prod.diametroexterior ?? "").trim();
  const alt = String(prod.altura ?? "").trim();
  const sec = String(prod.seccion ?? "").trim();

  const diametros =
    ordenDiametro === "exterior_interior" ? [de, di] : [di, de];

  // Si tiene altura usamos [D1, D2, Altura]; si no tiene altura pero sí sección (ej. O-rings), usamos sección
  const terceraMedida = alt || sec;
  const partes = [...diametros, terceraMedida].filter((p) => p !== "");

  return partes.length > 0 ? partes.join("x") : "-";
};

// Constantes fijas de tamaño para código de barras y textos (calibradas para albergar hasta 6 productos)
const MAX_PRODUCTOS_POR_CAJA = 6;
const BARCODE_FIXED_WIDTH = 1.0;
const BARCODE_FIXED_HEIGHT = 34;

const BoxBarcode = ({ value, width, height }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (wrapRef.current) {
      const svg = wrapRef.current.querySelector("svg");
      if (svg) {
        svg.setAttribute("preserveAspectRatio", "none");
        svg.style.height = `${height}px`;
      }
    }
  }, [value, width, height]);

  return (
    <div ref={wrapRef} className="ec-barcode-svg-wrap">
      <Barcode
        value={value}
        width={width}
        height={height}
        margin={0}
        displayValue={false}
      />
    </div>
  );
};

const EtiquetasCajaTab = () => {
  // Catálogos base
  const [catalogoProductosApi3, setCatalogoProductosApi3] = useState([]);
  const [stocksAlmacen, setStocksAlmacen] = useState([]);
  const [nombresAlmacenes, setNombresAlmacenes] = useState(ALMACENES_FALLBACK);
  const [almacenesDisponibles, setAlmacenesDisponibles] = useState([]);

  // Estados de carga
  const [cargandoInicial, setCargandoInicial] = useState(false);
  const [cargandoStocks, setCargandoStocks] = useState(false);
  const [cargandoFamilia, setCargandoFamilia] = useState(false);

  // Paso 1: Selección de Origen
  const [almacenSeleccionado, setAlmacenSeleccionado] = useState("");
  const [lineaSeleccionada, setLineaSeleccionada] = useState("");
  const [busquedaLinea, setBusquedaLinea] = useState("");
  const [familiaTexto, setFamiliaTexto] = useState("");
  const [seccionManualHeader, setSeccionManualHeader] = useState("");

  // Paso 2: Ordenamiento Jerárquico (deshabilitado para edición manual por el momento)
  const PASO_2_CONFIGURABLE = false;
  const [incluirSeccion, setIncluirSeccion] = useState(false);
  const [ordenDiametro, setOrdenDiametro] = useState("interior_exterior"); // 'interior_exterior' | 'exterior_interior'

  // Paso 3: Reglas dinámicas de capacidad en cascada
  const [reglasCapacidad, setReglasCapacidad] = useState([
    { id: "base-1", desdeCaja: 1, productosPorCaja: 4 },
  ]);
  const [nuevaReglaCaja, setNuevaReglaCaja] = useState("");
  const [nuevaReglaCantidad, setNuevaReglaCantidad] = useState(4);

  // Rango opcional de impresión de cajas
  const [cajaInicioPrint, setCajaInicioPrint] = useState(1);
  const [cajaFinPrint, setCajaFinPrint] = useState(9999);

  // Modalidad dentro del tab Etiquetas de Caja: 'automatico' | 'manual'
  const [modoGeneracion, setModoGeneracion] = useState("automatico");

  // Estados para Modo Manual (Armado Manual por Caja)
  const [lineaManual, setLineaManual] = useState("");
  const [busquedaLineaManual, setBusquedaLineaManual] = useState("");
  const [mostrarDropdownLineaManual, setMostrarDropdownLineaManual] = useState(false);
  const [cargandoLineaManual, setCargandoLineaManual] = useState(false);

  const [busquedaClaveManual, setBusquedaClaveManual] = useState("");
  const [mostrarDropdownClaveManual, setMostrarDropdownClaveManual] = useState(false);
  const [clavesCapturadasManual, setClavesCapturadasManual] = useState([]);
  const [numeroCajaManual, setNumeroCajaManual] = useState("1");
  const [cajasManualesGuardadas, setCajasManualesGuardadas] = useState([]);
  const [idCajaManualEditando, setIdCajaManualEditando] = useState(null);

  const wrapLineaManualRef = useRef(null);
  const wrapClaveManualRef = useRef(null);

  // Popover rápido en etiqueta de vista previa
  const [cajaEditandoInline, setCajaEditandoInline] = useState(null);

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle:
      modoGeneracion === "manual"
        ? `Etiquetas_Caja_Manual_${lineaManual || "Varias"}`
        : `Etiquetas_Caja_${lineaSeleccionada || "Linea"}_Alm${almacenSeleccionado}`,
    /* La orientación/márgenes de la hoja carta viven aquí (pageStyle) y no en el CSS
       compartido, para no forzar vertical a los demás tabs al imprimir */
    pageStyle: "@page { size: letter portrait; margin: 4.8mm 6.5mm 4mm 6.5mm; }",
  });

  // 1. Carga inicial: Catálogo ligero para mapear clave -> línea en el selector de Paso 1,
  // almacenes registrados en stocks_almacenes y catálogo de nombres de almacenes
  useEffect(() => {
    let cancelado = false;
    const cargarBase = async () => {
      setCargandoInicial(true);
      try {
        const [resProdsFirebird, resStocksAll, resAlmCat] =
          await Promise.allSettled([
            Axios.get(`${URL_API1}/compras/getcatalogo-firebird`).catch(() =>
              Axios.get(`${URL_API3}/productos-detallado`, {
                headers: API3_HEADERS,
              })
            ),
            Axios.get(`${URL_API2}/stocks-almacenes`),
            Axios.get(`${URL_API1}/compras/getalmacenes`),
          ]);

        if (cancelado) return;

        if (
          resProdsFirebird.status === "fulfilled" &&
          Array.isArray(resProdsFirebird.value.data)
        ) {
          setCatalogoProductosApi3(resProdsFirebird.value.data);
        } else {
          show_alerta("No se pudo cargar el catálogo de líneas desde SAE.", "error");
        }

        if (resAlmCat.status === "fulfilled" && Array.isArray(resAlmCat.value.data)) {
          const mapa = { ...ALMACENES_FALLBACK };
          resAlmCat.value.data.forEach((a) => {
            if (a.id !== undefined && a.nombre) {
              mapa[String(a.id).trim()] = String(a.nombre).trim();
            }
          });
          setNombresAlmacenes(mapa);
        }

        if (resStocksAll.status === "fulfilled") {
          const todos = resStocksAll.value.data?.body || [];
          const setAlm = new Set(
            todos
              .filter((r) => parseFloat(r.max) > 0)
              .map((r) => String(r.almacen ?? "").trim())
              .filter(Boolean)
          );
          const listaAlm = Array.from(setAlm).sort(
            (a, b) => parseFloat(a) - parseFloat(b)
          );
          setAlmacenesDisponibles(listaAlm);
          if (listaAlm.length > 0 && !almacenSeleccionado) {
            setAlmacenSeleccionado(listaAlm[0]);
          }
        }
      } catch (err) {
        console.error("Error cargando datos base para etiquetas de caja:", err);
      } finally {
        if (!cancelado) setCargandoInicial(false);
      }
    };

    cargarBase();
    return () => {
      cancelado = true;
    };
  }, []);

  // 2. Cuando cambia el almacén seleccionado, consultamos únicamente sus stocks (max > 0)
  useEffect(() => {
    if (!almacenSeleccionado) {
      setStocksAlmacen([]);
      return;
    }

    let cancelado = false;
    const cargarStocksDeAlmacen = async () => {
      setCargandoStocks(true);
      try {
        const res = await Axios.get(
          `${URL_API2}/stocks-almacenes?almacen=${encodeURIComponent(almacenSeleccionado)}`
        );
        if (cancelado) return;
        const lista = res.data?.body || [];
        // Regla de negocio base: productos cuyo max sea mayor a 0
        const conPresencia = lista.filter((r) => {
          const maxNum = parseFloat(r.max);
          return !Number.isNaN(maxNum) && maxNum > 0;
        });
        setStocksAlmacen(conPresencia);
      } catch (err) {
        if (!cancelado) {
          console.error("Error al consultar stocks del almacén:", err);
          show_alerta("Error al cargar los productos del almacén seleccionado.", "error");
        }
      } finally {
        if (!cancelado) setCargandoStocks(false);
      }
    };

    cargarStocksDeAlmacen();
    return () => {
      cancelado = true;
    };
  }, [almacenSeleccionado]);

  // Mapa de catálogo de productos por clave enriquecido desde POST /inventario/productos-etiquetas-caja
  const mapaProductosPorClave = useMemo(() => {
    const map = new Map();

    catalogoProductosApi3.forEach((p) => {
      const cve = String(p.CVE_ART ?? p.clave ?? "").trim().toUpperCase();
      if (!cve) return;
      const descr = String(p.DESCR ?? p.descripcion ?? "").trim();
      const lin = String(p.LIN_PROD ?? p.linea ?? "").trim().toUpperCase();
      const marcaDirecta = String(p.MARCA ?? p.marca ?? "").trim();

      map.set(cve, {
        clave: String(p.CVE_ART ?? p.clave ?? cve).trim(),
        descripcion: descr,
        linea: lin,
        diametrointerior: String(
          p.DIAM_INT ?? p["Diámetro Interior"] ?? p.diametrointerior ?? ""
        ).trim(),
        diametroexterior: String(
          p.DIAM_EXT ?? p["Diámetro Exterior"] ?? p.diametroexterior ?? ""
        ).trim(),
        altura: String(p.ALTURA ?? p.Altura ?? p.altura ?? "").trim(),
        seccion: String(p.SECCION ?? p.Seccion ?? p.seccion ?? "").trim(),
        perfil: String(p.PERFIL ?? p.Perfil ?? p.perfil ?? "").trim(),
        marca: marcaDirecta || inferirMarcaDesdeDescripcion(descr),
        familia: String(p.FAMILIA ?? p.Familia ?? p.CAT_ECOMM ?? p.familia ?? "").trim(),
        existenciasPorAlmacen:
          p.EXISTENCIA && typeof p.EXISTENCIA === "object" ? p.EXISTENCIA : null,
      });
    });

    return map;
  }, [catalogoProductosApi3]);

  // Productos del almacén seleccionado (desde stocks_almacenes con max > 0) enriquecidos con /inventario/productos-etiquetas-caja
  const productosEnAlmacen = useMemo(() => {
    const resultado = [];
    const clavesVistas = new Set();

    stocksAlmacen.forEach((st) => {
      const cve = String(st.clave ?? "").trim().toUpperCase();
      if (!cve || clavesVistas.has(cve)) return;
      const prodCat = mapaProductosPorClave.get(cve);
      if (!prodCat) return;

      clavesVistas.add(cve);
      resultado.push({
        ...prodCat,
        clave: String(prodCat.clave || st.clave).trim(),
        linea: String(prodCat.linea || "").trim().toUpperCase(),
        maxStock: st.max,
        minStock: st.min,
        rotacion: st.rotacion,
      });
    });

    return resultado;
  }, [stocksAlmacen, mapaProductosPorClave]);

  // Líneas disponibles en el almacén seleccionado (con conteo de productos con max > 0)
  const lineasDisponibles = useMemo(() => {
    const conteoPorLinea = new Map();
    productosEnAlmacen.forEach((p) => {
      if (!p.linea) return;
      conteoPorLinea.set(p.linea, (conteoPorLinea.get(p.linea) || 0) + 1);
    });

    return Array.from(conteoPorLinea.entries())
      .map(([linea, total]) => ({ linea, total }))
      .sort((a, b) => a.linea.localeCompare(b.linea));
  }, [productosEnAlmacen]);

  const lineasFiltradasSelector = useMemo(() => {
    const q = busquedaLinea.trim().toUpperCase();
    if (!q) return lineasDisponibles;
    return lineasDisponibles.filter((item) => item.linea.includes(q));
  }, [lineasDisponibles, busquedaLinea]);

  // Productos de la línea y almacén seleccionados:
  // 1) Todos los productos de la línea que están en stocks_almacenes (max > 0)
  // 2) Más cualquier producto adicional de la misma línea que no esté en stocks_almacenes pero tenga EXISTENCIA > 0 en este almacén
  const productosLineaCrudos = useMemo(() => {
    if (!lineaSeleccionada) return [];
    const base = productosEnAlmacen.filter((p) => p.linea === lineaSeleccionada);
    const clavesEnBase = new Set(
      base.map((p) => String(p.clave || "").trim().toUpperCase())
    );

    const extrasConExistencia = [];
    mapaProductosPorClave.forEach((prodCat, cve) => {
      if (prodCat.linea !== lineaSeleccionada || clavesEnBase.has(cve)) return;
      const existEnAlmacen = obtenerExistenciaEnAlmacen(
        prodCat.existenciasPorAlmacen,
        almacenSeleccionado,
        nombresAlmacenes
      );
      if (existEnAlmacen > 0) {
        clavesEnBase.add(cve);
        extrasConExistencia.push({
          ...prodCat,
          existenciaActual: existEnAlmacen,
        });
      }
    });

    console.log(
      `[EXCEPCIONES - Línea ${lineaSeleccionada} | Almacén ${almacenSeleccionado}] Productos con existencia fuera de stocks_almacenes (${extrasConExistencia.length}):`,
      extrasConExistencia
    );

    return [...base, ...extrasConExistencia];
  }, [
    productosEnAlmacen,
    lineaSeleccionada,
    mapaProductosPorClave,
    almacenSeleccionado,
    nombresAlmacenes,
  ]);

  // Detectar automáticamente si la línea seleccionada maneja la dimensión "sección"
  const infoSeccionLinea = useMemo(() => {
    if (productosLineaCrudos.length === 0) {
      return { tieneSeccion: false, cantidadConSeccion: 0 };
    }
    const conSeccion = productosLineaCrudos.filter(
      (p) => String(p.seccion ?? "").trim() !== ""
    );
    return {
      tieneSeccion: conSeccion.length > 0,
      cantidadConSeccion: conSeccion.length,
    };
  }, [productosLineaCrudos]);

  // Al cambiar de línea:
  // Consultar en POST /inventario/productos-etiquetas-caja enviando exclusivamente { linea: lineaSeleccionada }
  // para recibir el universo completo de productos de esa línea (con medidas, familia, perfil, marca y existencias por almacén)
  useEffect(() => {
    if (!lineaSeleccionada) {
      setFamiliaTexto("");
      setIncluirSeccion(false);
      return;
    }

    setReglasCapacidad([{ id: "base-1", desdeCaja: 1, productosPorCaja: 4 }]);
    setCajaEditandoInline(null);

    let cancelado = false;

    const cargarUniversoDeLinea = async () => {
      setCargandoFamilia(true);
      try {
        const universoLinea = await consultarProductosEtiquetasCajaPorLinea(
          lineaSeleccionada
        );
        if (cancelado || universoLinea.length === 0) return;

        const tieneSec = universoLinea.some(
          (item) => String(item.SECCION ?? item.Seccion ?? item.seccion ?? "").trim() !== ""
        );
        setIncluirSeccion(tieneSec);

        const famDetectada =
          universoLinea.find(
            (item) => String(item.FAMILIA ?? item.Familia ?? item.familia ?? "").trim() !== ""
          )?.FAMILIA || lineaSeleccionada;
        setFamiliaTexto(String(famDetectada).trim().toUpperCase());

        setCatalogoProductosApi3((prev) => {
          const mapaPrev = new Map();
          prev.forEach((item) => {
            const c = String(item.CVE_ART ?? item.clave ?? "").trim().toUpperCase();
            if (c) mapaPrev.set(c, item);
          });
          universoLinea.forEach((item) => {
            const c = String(item.CVE_ART ?? item.clave ?? "").trim().toUpperCase();
            if (c) mapaPrev.set(c, { ...(mapaPrev.get(c) || {}), ...item });
          });
          return Array.from(mapaPrev.values());
        });
      } catch (err) {
        console.warn(
          "Aviso al consultar universo de la línea en /inventario/productos-etiquetas-caja:",
          err.message
        );
      } finally {
        if (!cancelado) setCargandoFamilia(false);
      }
    };

    cargarUniversoDeLinea();

    return () => {
      cancelado = true;
    };
  }, [lineaSeleccionada]);

  // Paso 2: Ordenar jerárquicamente de menor a mayor los productos de la línea
  const productosOrdenados = useMemo(() => {
    if (productosLineaCrudos.length === 0) return [];

    const copia = [...productosLineaCrudos];
    copia.sort((a, b) => {
      // 1. Nivel: Sección (si está habilitado)
      if (incluirSeccion) {
        const secA = parseMedidaNumerica(a.seccion);
        const secB = parseMedidaNumerica(b.seccion);
        if (secA !== secB) return secA - secB;
      }

      // 2. Nivel: Diámetro prioritario y secundario
      const diA = parseMedidaNumerica(a.diametrointerior);
      const diB = parseMedidaNumerica(b.diametrointerior);
      const deA = parseMedidaNumerica(a.diametroexterior);
      const deB = parseMedidaNumerica(b.diametroexterior);

      if (ordenDiametro === "interior_exterior") {
        if (diA !== diB) return diA - diB;
        if (deA !== deB) return deA - deB;
      } else {
        if (deA !== deB) return deA - deB;
        if (diA !== diB) return diA - diB;
      }

      // 3. Nivel fijo: Altura
      const altA = parseMedidaNumerica(a.altura);
      const altB = parseMedidaNumerica(b.altura);
      if (altA !== altB) return altA - altB;

      // Desempate alfabético por clave
      return String(a.clave).localeCompare(String(b.clave));
    });

    return copia;
  }, [productosLineaCrudos, incluirSeccion, ordenDiametro]);

  // Normalizar y ordenar reglas de capacidad por desdeCaja ascendente
  const reglasOrdenadas = useMemo(() => {
    const copia = [...reglasCapacidad]
      .map((r) => ({
        ...r,
        desdeCaja: Math.max(1, parseInt(r.desdeCaja, 10) || 1),
        productosPorCaja: Math.max(
          1,
          Math.min(MAX_PRODUCTOS_POR_CAJA, parseInt(r.productosPorCaja, 10) || 4)
        ),
      }))
      .sort((a, b) => a.desdeCaja - b.desdeCaja);

    // Asegurar que siempre exista la regla desdeCaja = 1
    if (copia.length === 0 || copia[0].desdeCaja !== 1) {
      copia.unshift({ id: "base-1", desdeCaja: 1, productosPorCaja: 4 });
    }
    return copia;
  }, [reglasCapacidad]);

  // Paso 3: Construir las cajas aplicando las reglas en cascada
  const { cajasGeneradas, resumenTramos } = useMemo(() => {
    if (productosOrdenados.length === 0) {
      return { cajasGeneradas: [], resumenTramos: [] };
    }

    const cajas = [];
    let cursorProducto = 0;
    let numeroCaja = 1;

    while (cursorProducto < productosOrdenados.length) {
      // Buscar la regla activa para este número de caja (la última con desdeCaja <= numeroCaja)
      let reglaActiva = reglasOrdenadas[0];
      for (let i = 0; i < reglasOrdenadas.length; i++) {
        if (reglasOrdenadas[i].desdeCaja <= numeroCaja) {
          reglaActiva = reglasOrdenadas[i];
        } else {
          break;
        }
      }

      const capacidad = Math.max(1, reglaActiva.productosPorCaja);
      const lote = productosOrdenados.slice(cursorProducto, cursorProducto + capacidad);

      // Marcas y perfiles únicos (sin repetir palabras) presentes en la caja
      const marcasUnicas = Array.from(
        new Set(
          lote
            .map((p) => String(p.marca ?? "").trim().toUpperCase())
            .filter(Boolean)
        )
      );
      const perfilesUnicos = Array.from(
        new Set(
          lote
            .map((p) => String(p.perfil ?? "").trim().toUpperCase())
            .filter(Boolean)
        )
      );

      // Secciones presentes en la caja
      const seccionesUnicas = Array.from(
        new Set(
          lote
            .map((p) => String(p.seccion ?? "").trim())
            .filter(Boolean)
        )
      );

      // Familias únicas presentes en la caja
      const familiasUnicas = Array.from(
        new Set(
          lote
            .map((p) => String(p.familia ?? "").trim().toUpperCase())
            .filter(Boolean)
        )
      );

      const esInicioDeTramo = reglasOrdenadas.some(
        (r) => r.desdeCaja === numeroCaja
      );

      cajas.push({
        id: `auto-${numeroCaja}`,
        esManual: false,
        linea: lineaSeleccionada,
        numeroCaja,
        capacidadAplicada: capacidad,
        esInicioDeTramo,
        productos: lote,
        familiaTexto: familiasUnicas.join(", ") || familiaTexto,
        marcasTexto: marcasUnicas.join(", "),
        perfilesTexto: perfilesUnicos.join(", "),
        seccionAuto: seccionesUnicas.join(", "),
      });

      cursorProducto += lote.length;
      numeroCaja += 1;
    }

    const totalCajas = cajas.length;

    // Calcular el rango resultante para cada regla en el panel constructor
    const tramos = reglasOrdenadas.map((regla, idx) => {
      const siguienteRegla = reglasOrdenadas[idx + 1];
      const hastaCaja = siguienteRegla
        ? Math.min(totalCajas, siguienteRegla.desdeCaja - 1)
        : totalCajas;
      const cajasEnTramo = cajas.filter(
        (c) =>
          c.numeroCaja >= regla.desdeCaja &&
          (siguienteRegla ? c.numeroCaja < siguienteRegla.desdeCaja : true)
      );
      const totalProdsTramo = cajasEnTramo.reduce(
        (acc, c) => acc + c.productos.length,
        0
      );

      return {
        ...regla,
        hastaCaja,
        activa: regla.desdeCaja <= totalCajas,
        totalCajasTramo: cajasEnTramo.length,
        totalProdsTramo,
      };
    });

    return { cajasGeneradas: cajas, resumenTramos: tramos };
  }, [productosOrdenados, reglasOrdenadas]);

  // Ajustar el rango de impresión cuando cambia el total de cajas
  useEffect(() => {
    setCajaInicioPrint(1);
    setCajaFinPrint(cajasGeneradas.length || 1);
  }, [cajasGeneradas.length, lineaSeleccionada]);

  // Filtrar cajas por rango de impresión elegido y agrupar de 4 en 4 por hoja tamaño carta
  const cajasParaImprimir = useMemo(() => {
    const ini = Math.max(1, parseInt(cajaInicioPrint, 10) || 1);
    const fin = Math.max(ini, parseInt(cajaFinPrint, 10) || cajasGeneradas.length);
    return cajasGeneradas.filter(
      (c) => c.numeroCaja >= ini && c.numeroCaja <= fin
    );
  }, [cajasGeneradas, cajaInicioPrint, cajaFinPrint]);

  const hojasCarta = useMemo(() => {
    const hojas = [];
    for (let i = 0; i < cajasParaImprimir.length; i += 4) {
      hojas.push(cajasParaImprimir.slice(i, i + 4));
    }
    return hojas;
  }, [cajasParaImprimir]);

  // Funciones para gestionar reglas de capacidad en cascada
  const definirCapacidadDesdeCaja = (numCaja, cantidadProductos) => {
    const cajaInt = Math.max(1, parseInt(numCaja, 10) || 1);
    const cantInt = Math.max(
      1,
      Math.min(MAX_PRODUCTOS_POR_CAJA, parseInt(cantidadProductos, 10) || 4)
    );

    setReglasCapacidad((prev) => {
      const existe = prev.find((r) => parseInt(r.desdeCaja, 10) === cajaInt);
      if (existe) {
        return prev.map((r) =>
          parseInt(r.desdeCaja, 10) === cajaInt
            ? { ...r, productosPorCaja: cantInt }
            : r
        );
      }
      return [
        ...prev,
        {
          id: `tramo-${Date.now()}-${cajaInt}`,
          desdeCaja: cajaInt,
          productosPorCaja: cantInt,
        },
      ];
    });
    setCajaEditandoInline(null);
  };

  const cambiarDesdeCajaEnRegla = (idRegla, nuevoDesdeCaja) => {
    const val = Math.max(2, parseInt(nuevoDesdeCaja, 10) || 2);
    setReglasCapacidad((prev) =>
      prev.map((r) => (r.id === idRegla ? { ...r, desdeCaja: val } : r))
    );
  };

  const eliminarRegla = (idRegla) => {
    setReglasCapacidad((prev) => prev.filter((r) => r.id !== idRegla));
  };

  const handleAgregarNuevaReglaManual = (e) => {
    e.preventDefault();
    const numCaja = parseInt(nuevaReglaCaja, 10);
    if (!numCaja || numCaja < 1) {
      show_alerta("Indique un número de caja válido (mayor o igual a 1).", "warning");
      return;
    }
    definirCapacidadDesdeCaja(numCaja, nuevaReglaCantidad);
    setNuevaReglaCaja("");
  };

  const reiniciarReglas = () => {
    setReglasCapacidad([{ id: "base-1", desdeCaja: 1, productosPorCaja: 4 }]);
    setCajaEditandoInline(null);
  };

  // ============================================================================
  // LÓGICA DE MODO MANUAL (ARMADO MANUAL POR CAJA)
  // ============================================================================

  // Cerrar dropdowns de autocompletado manual al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapLineaManualRef.current && !wrapLineaManualRef.current.contains(e.target)) {
        setMostrarDropdownLineaManual(false);
      }
      if (wrapClaveManualRef.current && !wrapClaveManualRef.current.contains(e.target)) {
        setMostrarDropdownClaveManual(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Universo completo de líneas en todo el catálogo (sin restringir por almacén)
  const todasLasLineasCatalogo = useMemo(() => {
    const conteo = new Map();
    catalogoProductosApi3.forEach((p) => {
      const lin = String(p.LIN_PROD ?? p.linea ?? "").trim().toUpperCase();
      if (!lin) return;
      conteo.set(lin, (conteo.get(lin) || 0) + 1);
    });
    return Array.from(conteo.entries())
      .map(([linea, total]) => ({ linea, total }))
      .sort((a, b) => a.linea.localeCompare(b.linea));
  }, [catalogoProductosApi3]);

  // Sugerencias de línea para el autocompletado manual
  const sugerenciasLineaManual = useMemo(() => {
    const q = busquedaLineaManual.trim().toUpperCase();
    if (!q) return todasLasLineasCatalogo.slice(0, 40);
    return todasLasLineasCatalogo
      .filter((item) => item.linea.includes(q))
      .slice(0, 40);
  }, [todasLasLineasCatalogo, busquedaLineaManual]);

  // Al elegir una línea en Modo Manual, consultar POST /inventario/productos-etiquetas-caja
  // para obtener FAMILIA, MARCA, PERFIL, SECCION y medidas de cada producto de esa línea
  const seleccionarLineaManual = async (lineaElegida) => {
    const lin = String(lineaElegida || "").trim().toUpperCase();
    if (!lin) return;
    setLineaManual(lin);
    setBusquedaLineaManual(lin);
    setMostrarDropdownLineaManual(false);
    setBusquedaClaveManual("");

    setCargandoLineaManual(true);
    try {
      const universoLinea = await consultarProductosEtiquetasCajaPorLinea(lin);
      if (universoLinea.length > 0) {
        setCatalogoProductosApi3((prev) => {
          const mapaPrev = new Map();
          prev.forEach((item) => {
            const c = String(item.CVE_ART ?? item.clave ?? "").trim().toUpperCase();
            if (c) mapaPrev.set(c, item);
          });
          universoLinea.forEach((item) => {
            const c = String(item.CVE_ART ?? item.clave ?? "").trim().toUpperCase();
            if (c) mapaPrev.set(c, { ...(mapaPrev.get(c) || {}), ...item });
          });
          return Array.from(mapaPrev.values());
        });
      }
    } catch (err) {
      console.warn(
        "Aviso al consultar productos de la línea manual en /inventario/productos-etiquetas-caja:",
        err.message
      );
    } finally {
      setCargandoLineaManual(false);
    }
  };

  // Universo de productos pertenecientes a la línea seleccionada en Modo Manual
  const productosDeLineaManual = useMemo(() => {
    if (!lineaManual) return [];
    const lista = [];
    mapaProductosPorClave.forEach((prod) => {
      if (prod.linea === lineaManual) {
        lista.push(prod);
      }
    });
    return lista.sort((a, b) => String(a.clave).localeCompare(String(b.clave)));
  }, [mapaProductosPorClave, lineaManual]);

  // Sugerencias de claves para el autocompletado manual (excluyendo las ya capturadas en la caja actual)
  const sugerenciasClaveManual = useMemo(() => {
    if (!lineaManual) return [];
    const clavesYaEnCaja = new Set(
      clavesCapturadasManual.map((p) => String(p.clave || "").trim().toUpperCase())
    );
    const q = busquedaClaveManual.trim().toUpperCase();

    return productosDeLineaManual
      .filter((prod) => {
        const cveUp = String(prod.clave || "").trim().toUpperCase();
        if (clavesYaEnCaja.has(cveUp)) return false;
        if (!q) return true;
        const descrUp = String(prod.descripcion || "").toUpperCase();
        return cveUp.includes(q) || descrUp.includes(q);
      })
      .slice(0, 30);
  }, [productosDeLineaManual, clavesCapturadasManual, busquedaClaveManual, lineaManual]);

  // Agregar clave seleccionada al arreglo de la caja manual (respetando orden de captura y tope de 6)
  const agregarClaveACajaManual = (prod) => {
    if (!prod || !prod.clave) return;
    if (clavesCapturadasManual.length >= MAX_PRODUCTOS_POR_CAJA) {
      show_alerta(`Solo se permiten hasta ${MAX_PRODUCTOS_POR_CAJA} claves por caja.`, "warning");
      return;
    }
    const cveUp = String(prod.clave).trim().toUpperCase();
    if (
      clavesCapturadasManual.some(
        (item) => String(item.clave).trim().toUpperCase() === cveUp
      )
    ) {
      show_alerta("Esta clave ya fue agregada a la caja actual.", "warning");
      return;
    }

    setClavesCapturadasManual((prev) => [...prev, prod]);
    setBusquedaClaveManual("");
    setMostrarDropdownClaveManual(false);
  };

  // Eliminar una clave de la caja manual actual
  const eliminarClaveDeCajaManual = (clave) => {
    const cveUp = String(clave || "").trim().toUpperCase();
    setClavesCapturadasManual((prev) =>
      prev.filter((item) => String(item.clave).trim().toUpperCase() !== cveUp)
    );
  };

  // Mover una clave hacia arriba (-1) o hacia abajo (+1) en la lista manual
  const moverClaveEnCajaManual = (index, direccion) => {
    setClavesCapturadasManual((prev) => {
      const destino = index + direccion;
      if (destino < 0 || destino >= prev.length) return prev;
      const copia = [...prev];
      const temp = copia[index];
      copia[index] = copia[destino];
      copia[destino] = temp;
      return copia;
    });
  };

  // Construye el objeto de etiqueta de caja manual obteniendo FAMILIA, MARCA, PERFIL y SECCION
  // directamente de los productos capturados en esa caja
  const construirCajaManualDesdeProductos = ({
    id,
    linea,
    numeroCaja,
    productosBase,
    enCaptura = false,
    enEdicion = false,
  }) => {
    const productosActualizados = (productosBase || []).map((p) => {
      const cveUp = String(p.clave || "").trim().toUpperCase();
      return mapaProductosPorClave.get(cveUp) || p;
    });

    const familiasUnicas = Array.from(
      new Set(
        productosActualizados
          .map((p) => String(p.familia ?? "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
    const marcasUnicas = Array.from(
      new Set(
        productosActualizados
          .map((p) => String(p.marca ?? "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
    const perfilesUnicos = Array.from(
      new Set(
        productosActualizados
          .map((p) => String(p.perfil ?? "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
    const seccionesUnicas = Array.from(
      new Set(
        productosActualizados
          .map((p) => String(p.seccion ?? "").trim())
          .filter(Boolean)
      )
    );

    const lineaFinal =
      String(linea || productosActualizados[0]?.linea || "").trim().toUpperCase();

    return {
      id: id || `manual-${Date.now()}`,
      esManual: true,
      enCaptura,
      enEdicion,
      linea: lineaFinal,
      numeroCaja: String(numeroCaja || "1").trim() || "1",
      capacidadAplicada: productosActualizados.length,
      esInicioDeTramo: false,
      productos: productosActualizados,
      familiaTexto: familiasUnicas.join(", ") || lineaFinal,
      marcasTexto: marcasUnicas.join(", "),
      perfilesTexto: perfilesUnicos.join(", "),
      seccionAuto: seccionesUnicas.join(", "),
    };
  };

  // Guardar la caja manual actual en la cola de la hoja carta y preparar la siguiente
  const guardarCajaManualEnHoja = () => {
    if (!lineaManual) {
      show_alerta("Seleccione primero una línea.", "warning");
      return;
    }
    if (clavesCapturadasManual.length === 0) {
      show_alerta("Agregue al menos una clave a la caja.", "warning");
      return;
    }
    const numLimpio = String(numeroCajaManual || "").trim();
    if (!numLimpio) {
      show_alerta("Capture el número de caja.", "warning");
      return;
    }

    if (idCajaManualEditando) {
      setCajasManualesGuardadas((prev) =>
        prev.map((c) =>
          c.id === idCajaManualEditando
            ? {
                id: c.id,
                linea: lineaManual,
                numeroCaja: numLimpio,
                productos: clavesCapturadasManual,
              }
            : c
        )
      );
      setIdCajaManualEditando(null);
    } else {
      const nuevaId = `caja-man-${Date.now()}`;
      setCajasManualesGuardadas((prev) => [
        ...prev,
        {
          id: nuevaId,
          linea: lineaManual,
          numeroCaja: numLimpio,
          productos: clavesCapturadasManual,
        },
      ]);
    }

    // Limpiar claves para capturar la siguiente caja y sugerir el siguiente número consecutivo mayor
    setClavesCapturadasManual([]);
    setBusquedaClaveManual("");
    const maxPrevio = cajasManualesGuardadas.reduce((acc, c) => {
      const n = parseInt(c.numeroCaja, 10);
      return !Number.isNaN(n) && n > acc ? n : acc;
    }, 0);
    const parsedNum = parseInt(numLimpio, 10);
    const maxTotal = Math.max(
      maxPrevio,
      !Number.isNaN(parsedNum) ? parsedNum : 0
    );
    if (maxTotal > 0) {
      setNumeroCajaManual(String(maxTotal + 1));
    }
  };

  // Cargar una caja guardada en el formulario para editarla
  // Si había una caja nueva en captura (con claves) o se estaba editando otra caja distinta,
  // la persiste automáticamente en cajasManualesGuardadas para que ninguna caja desaparezca.
  const editarCajaManualGuardada = (cajaGuardada) => {
    const registro = cajasManualesGuardadas.find((c) => c.id === cajaGuardada.id);
    if (!registro) return;
    if (idCajaManualEditando === registro.id) return;

    const numActualLimpio = String(numeroCajaManual || "1").trim() || "1";

    if (!idCajaManualEditando && clavesCapturadasManual.length > 0 && lineaManual) {
      const nuevaCajaAutoGuardada = {
        id: `caja-man-${Date.now()}`,
        linea: lineaManual,
        numeroCaja: numActualLimpio,
        productos: clavesCapturadasManual,
      };
      setCajasManualesGuardadas((prev) => [...prev, nuevaCajaAutoGuardada]);
    } else if (
      idCajaManualEditando &&
      idCajaManualEditando !== registro.id &&
      clavesCapturadasManual.length > 0
    ) {
      const idPrevio = idCajaManualEditando;
      const lineaPrevia = lineaManual;
      const clavesPrevias = clavesCapturadasManual;
      setCajasManualesGuardadas((prev) =>
        prev.map((c) =>
          c.id === idPrevio
            ? {
                ...c,
                linea: lineaPrevia || c.linea,
                numeroCaja: numActualLimpio,
                productos: clavesPrevias,
              }
            : c
        )
      );
    }

    setIdCajaManualEditando(registro.id);
    setLineaManual(registro.linea);
    setBusquedaLineaManual(registro.linea);
    setNumeroCajaManual(registro.numeroCaja);
    setClavesCapturadasManual(registro.productos);
    seleccionarLineaManual(registro.linea);
  };

  const cancelarEdicionCajaManual = () => {
    // Si estaba editando una caja, preservar sus cambios actuales antes de pasar a capturar una nueva
    if (idCajaManualEditando && clavesCapturadasManual.length > 0) {
      const idActual = idCajaManualEditando;
      const numActualLimpio = String(numeroCajaManual || "1").trim() || "1";
      const lineaActual = lineaManual;
      const clavesActuales = clavesCapturadasManual;
      setCajasManualesGuardadas((prev) =>
        prev.map((c) =>
          c.id === idActual
            ? {
                ...c,
                linea: lineaActual || c.linea,
                numeroCaja: numActualLimpio,
                productos: clavesActuales,
              }
            : c
        )
      );
    }
    setIdCajaManualEditando(null);
    setClavesCapturadasManual([]);
    setBusquedaClaveManual("");
    const maxNum = cajasManualesGuardadas.reduce((acc, c) => {
      const n = parseInt(c.numeroCaja, 10);
      return !Number.isNaN(n) && n > acc ? n : acc;
    }, 0);
    setNumeroCajaManual(maxNum > 0 ? String(maxNum + 1) : "1");
  };

  const eliminarCajaManualGuardada = (idCaja) => {
    setCajasManualesGuardadas((prev) => prev.filter((c) => c.id !== idCaja));
    if (idCajaManualEditando === idCaja) {
      setIdCajaManualEditando(null);
      setClavesCapturadasManual([]);
    }
  };

  // Lista combinada de cajas manuales para la Vista Previa Tamaño Carta:
  // Incluye las cajas ya guardadas (actualizando en vivo si alguna está en edición)
  // más la caja que el usuario está capturando actualmente si tiene al menos 1 clave.
  const cajasManualesVistaPrevia = useMemo(() => {
    const lista = cajasManualesGuardadas.map((c) => {
      if (c.id === idCajaManualEditando) {
        return construirCajaManualDesdeProductos({
          id: c.id,
          linea: lineaManual || c.linea,
          numeroCaja: numeroCajaManual || c.numeroCaja,
          productosBase:
            clavesCapturadasManual.length > 0 ? clavesCapturadasManual : c.productos,
          enEdicion: true,
        });
      }
      return construirCajaManualDesdeProductos({
        id: c.id,
        linea: c.linea,
        numeroCaja: c.numeroCaja,
        productosBase: c.productos,
      });
    });

    if (!idCajaManualEditando && clavesCapturadasManual.length > 0) {
      lista.push(
        construirCajaManualDesdeProductos({
          id: "borrador-en-captura",
          linea: lineaManual,
          numeroCaja: numeroCajaManual,
          productosBase: clavesCapturadasManual,
          enCaptura: true,
        })
      );
    }

    return lista;
  }, [
    cajasManualesGuardadas,
    idCajaManualEditando,
    lineaManual,
    numeroCajaManual,
    clavesCapturadasManual,
    mapaProductosPorClave,
  ]);

  // Cajas y Hojas Carta activas según el modo seleccionado ('automatico' vs 'manual')
  const cajasActivasVistaPrevia =
    modoGeneracion === "manual" ? cajasManualesVistaPrevia : cajasParaImprimir;

  const hojasActivasVistaPrevia = useMemo(() => {
    const hojas = [];
    for (let i = 0; i < cajasActivasVistaPrevia.length; i += 4) {
      hojas.push(cajasActivasVistaPrevia.slice(i, i + 4));
    }
    return hojas;
  }, [cajasActivasVistaPrevia]);

  const cargandoVistaPrevia =
    modoGeneracion === "manual"
      ? cargandoLineaManual && cajasActivasVistaPrevia.length === 0
      : cargandoFamilia;

  return (
    <div className="etiquetas-caja-workspace">
      {/* SELECTOR DE MODALIDAD: AUTOMÁTICO POR LÍNEA VS ARMADO MANUAL POR CAJA */}
      <div className="ec-mode-switcher-bar">
        <span className="ec-field-label mb-0">Modalidad de generación:</span>
        <div className="ec-segmented-control" role="group">
          <button
            type="button"
            className={`ec-segmented-item ${
              modoGeneracion === "automatico" ? "active" : ""
            }`}
            onClick={() => setModoGeneracion("automatico")}
          >
            Por Línea (Automático)
          </button>
          <button
            type="button"
            className={`ec-segmented-item ${
              modoGeneracion === "manual" ? "active" : ""
            }`}
            onClick={() => setModoGeneracion("manual")}
          >
            Armado Manual por Caja
          </button>
        </div>
      </div>

      {/* BARRA SUPERIOR DE PASOS 1 Y 2 (SOLO EN MODO AUTOMÁTICO) */}
      {modoGeneracion === "automatico" && (
      <div className="ec-config-grid">
        {/* PASO 1: ORIGEN (ALMACÉN Y LÍNEA EN COLUMNA VERTICAL) */}
        <div className="ec-card ec-step1-card">
          <div className="ec-card-header">
            <span className="ec-step-badge">Paso 1</span>
            <span className="ec-card-title">
              <BiPackage className="me-1" />
              Selección de Almacén y Línea
            </span>
          </div>
          <div className="ec-card-body">
            <div className="d-flex flex-column gap-2">
              {/* Select Almacén */}
              <div>
                <label className="form-label ec-field-label mb-1">
                  Almacén
                </label>
                <select
                  className="form-select form-select-sm ec-control-sm"
                  value={almacenSeleccionado}
                  onChange={(e) => {
                    setAlmacenSeleccionado(e.target.value);
                    setLineaSeleccionada("");
                  }}
                  disabled={cargandoInicial}
                >
                  <option value="">-- Seleccionar --</option>
                  {almacenesDisponibles.map((alm) => (
                    <option key={alm} value={alm}>
                      Almacén {alm}
                      {nombresAlmacenes[alm] ? ` - ${nombresAlmacenes[alm]}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Buscador + Select de Línea */}
              <div>
                <label className="form-label ec-field-label mb-1 d-flex align-items-center justify-content-between">
                  <span>Línea a imprimir</span>
                  {(cargandoInicial || cargandoStocks) && almacenSeleccionado && (
                    <span className="ec-inline-loader text-primary">
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Cargando líneas...
                    </span>
                  )}
                </label>
                <div className="input-group input-group-sm">
                  <input
                    type="text"
                    className="form-control ec-control-sm"
                    placeholder="Filtrar línea..."
                    value={busquedaLinea}
                    onChange={(e) => setBusquedaLinea(e.target.value)}
                    style={{ maxWidth: "105px" }}
                    disabled={!almacenSeleccionado || cargandoInicial || cargandoStocks}
                  />
                  <select
                    className="form-select ec-control-sm"
                    value={lineaSeleccionada}
                    onChange={(e) => setLineaSeleccionada(e.target.value)}
                    disabled={!almacenSeleccionado || cargandoInicial || cargandoStocks}
                  >
                    <option value="">
                      {cargandoInicial || cargandoStocks
                        ? "Obteniendo líneas del almacén..."
                        : "-- Elegir Línea --"}
                    </option>
                    {lineasFiltradasSelector.map((item) => (
                      <option key={item.linea} value={item.linea}>
                        {item.linea} ({item.total} prods)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PASO 2: JERARQUÍA DE ORDENAMIENTO DIMENSIONAL */}
        <div className="ec-card ec-step2-card">
          <div className="ec-card-header">
            <span className="ec-step-badge">Paso 2</span>
            <span className="ec-card-title">
              <BiSortAlt2 className="me-1" />
              Jerarquía de Ordenamiento (Menor a Mayor)
            </span>
          </div>
          <div className="ec-card-body d-flex flex-column justify-content-center">
            <div className="ec-step2-controls">
              {/* 1er Nivel: Sección */}
              <div className="ec-seccion-box">
                <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                  <input
                    className="form-check-input mt-0"
                    type="checkbox"
                    id="switchIncluirSeccion"
                    checked={incluirSeccion}
                    onChange={(e) => {
                      if (PASO_2_CONFIGURABLE) setIncluirSeccion(e.target.checked);
                    }}
                    disabled={!PASO_2_CONFIGURABLE || !lineaSeleccionada}
                  />
                  <label
                    className="form-check-label ec-field-label mb-0"
                    htmlFor="switchIncluirSeccion"
                  >
                    Incluir Sección como 1er criterio
                  </label>
                </div>
                {lineaSeleccionada && !cargandoFamilia && (
                  <span
                    className={`ec-sec-status ${
                      infoSeccionLinea.tieneSeccion ? "ec-sec-detected" : "ec-sec-empty"
                    }`}
                  >
                    {infoSeccionLinea.tieneSeccion ? (
                      <>
                        <BiCheckCircle className="me-1" />
                        {infoSeccionLinea.cantidadConSeccion} productos con sección
                      </>
                    ) : (
                      "Sin sección en esta línea"
                    )}
                  </span>
                )}
              </div>

              {/* 2do Nivel: Prioridad Diámetro Interior vs Exterior (Segmented Control Neutro) */}
              <div className="ec-diam-selector">
                <span className="ec-diam-label">Prioridad de diámetros:</span>
                <div className="ec-segmented-control" role="group">
                  <button
                    type="button"
                    className={`ec-segmented-item ${
                      ordenDiametro === "interior_exterior" ? "active" : ""
                    }`}
                    onClick={() => {
                      if (PASO_2_CONFIGURABLE) setOrdenDiametro("interior_exterior");
                    }}
                    disabled={!PASO_2_CONFIGURABLE || !lineaSeleccionada}
                  >
                    Diám. Interior → Exterior
                  </button>
                  <button
                    type="button"
                    className={`ec-segmented-item ${
                      ordenDiametro === "exterior_interior" ? "active" : ""
                    }`}
                    onClick={() => {
                      if (PASO_2_CONFIGURABLE) setOrdenDiametro("exterior_interior");
                    }}
                    disabled={!PASO_2_CONFIGURABLE || !lineaSeleccionada}
                  >
                    Diám. Exterior → Interior
                  </button>
                </div>
              </div>

              {/* 3er Nivel: Resumen visual de la jerarquía activa */}
              <div className="ec-pipeline-row">
                <span className="ec-diam-label">Secuencia activa:</span>
                <div className="ec-pipeline-pills">
                  {incluirSeccion && (
                    <>
                      <span className="ec-order-chip ec-chip-sec">1° Sección</span>
                      <span className="ec-chip-arrow">→</span>
                    </>
                  )}
                  <span className="ec-order-chip ec-chip-main">
                    {incluirSeccion ? "2°" : "1°"}{" "}
                    {ordenDiametro === "interior_exterior" ? "Diám. Interior" : "Diám. Exterior"}
                  </span>
                  <span className="ec-chip-arrow">→</span>
                  <span className="ec-order-chip ec-chip-main">
                    {incluirSeccion ? "3°" : "2°"}{" "}
                    {ordenDiametro === "interior_exterior" ? "Diám. Exterior" : "Diám. Interior"}
                  </span>
                  <span className="ec-chip-arrow">→</span>
                  <span className="ec-order-chip ec-chip-fixed">
                    {incluirSeccion ? "4°" : "3°"} Altura
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* CONTENIDO PRINCIPAL: MODO AUTOMÁTICO O MODO MANUAL + VISTA PREVIA HOJA CARTA 2x2 */}
      {modoGeneracion === "automatico" && !lineaSeleccionada ? (
        <div className="ec-empty-state">
          <BiLayer size={36} className="text-secondary mb-2" />
          <div className="ec-empty-title">
            Seleccione un Almacén y una Línea para generar las etiquetas de caja
          </div>
          <p className="ec-helper-text text-muted mb-0">
            Se cargarán automáticamente los productos con stock máximo &gt; 0 en ese almacén,
            ordenados de menor a mayor y agrupados inicialmente de 4 productos por caja.
          </p>
        </div>
      ) : (
        <div className="ec-main-split">
          {/* COLUMNA IZQUIERDA: PASO 3 (AUTOMÁTICO) O CONSTRUCTOR MANUAL DE CAJAS */}
          <div className="ec-rules-panel">
            {modoGeneracion === "manual" ? (
              <div className="ec-card h-100">
                <div className="ec-card-header d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span className="ec-step-badge">Manual</span>
                    <span className="ec-card-title">
                      <BiPackage className="me-1" />
                      Constructor Manual de Caja
                    </span>
                  </div>
                  {idCajaManualEditando && (
                    <span className="badge bg-warning text-dark" style={{ fontSize: "0.68rem" }}>
                      Editando caja
                    </span>
                  )}
                </div>

                <div className="ec-card-body d-flex flex-column gap-3">
                  {/* 1. BUSCADOR DE LÍNEA CON AUTOCOMPLETADO (UNIVERSO COMPLETO) */}
                  <div ref={wrapLineaManualRef} className=" position-relative">
                    <label className="form-label ec-field-label mb-1 d-flex align-items-center justify-content-between">
                      <span>1. Línea (todo el catálogo)</span>
                      {(cargandoInicial || cargandoLineaManual) && (
                        <span className="ec-inline-loader text-primary">
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          {cargandoInicial ? "Cargando catálogo..." : "Cargando línea..."}
                        </span>
                      )}
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light text-secondary">
                        <BiSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control ec-control-sm"
                        placeholder="Escriba para buscar línea (ej. ORSN7, MPL)..."
                        value={busquedaLineaManual}
                        onFocus={() => setMostrarDropdownLineaManual(true)}
                        onChange={(e) => {
                          const val = e.target.value;
                          setBusquedaLineaManual(val);
                          setMostrarDropdownLineaManual(true);
                          if (!val.trim()) {
                            setLineaManual("");
                          }
                        }}
                        disabled={cargandoInicial}
                      />
                      {busquedaLineaManual && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary ec-control-sm"
                          onClick={() => {
                            setBusquedaLineaManual("");
                            setLineaManual("");
                            setMostrarDropdownLineaManual(true);
                          }}
                          title="Limpiar línea"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {mostrarDropdownLineaManual && sugerenciasLineaManual.length > 0 && (
                      <div className="ec-autocomplete-dropdown">
                        {sugerenciasLineaManual.map((item) => (
                          <div
                            key={item.linea}
                            className={`ec-autocomplete-item ${
                              item.linea === lineaManual ? "active" : ""
                            }`}
                            onClick={() => seleccionarLineaManual(item.linea)}
                          >
                            <span className="fw-semibold">{item.linea}</span>
                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                              {item.total} prods
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 2. AUTOCOMPLETADO DE CLAVE (MÁXIMO 6 POR CAJA) */}
                  <div ref={wrapClaveManualRef} className="position-relative">
                    <label className="form-label ec-field-label mb-1 d-flex align-items-center justify-content-between">
                      <span>2. Elegir claves de la caja</span>
                      <span
                        className={`badge ${
                          clavesCapturadasManual.length >= MAX_PRODUCTOS_POR_CAJA
                            ? "bg-danger"
                            : "bg-secondary"
                        }`}
                        style={{ fontSize: "0.68rem" }}
                      >
                        {clavesCapturadasManual.length} / {MAX_PRODUCTOS_POR_CAJA} claves
                      </span>
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light text-secondary">
                        <BiSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control ec-control-sm"
                        placeholder={
                          !lineaManual
                            ? "Seleccione primero una línea..."
                            : clavesCapturadasManual.length >= MAX_PRODUCTOS_POR_CAJA
                            ? "Límite de 6 claves alcanzado"
                            : "Buscar clave en la línea..."
                        }
                        value={busquedaClaveManual}
                        onFocus={() => setMostrarDropdownClaveManual(true)}
                        onChange={(e) => {
                          setBusquedaClaveManual(e.target.value);
                          setMostrarDropdownClaveManual(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && sugerenciasClaveManual.length > 0) {
                            e.preventDefault();
                            agregarClaveACajaManual(sugerenciasClaveManual[0]);
                          }
                        }}
                        disabled={
                          !lineaManual ||
                          cargandoLineaManual ||
                          clavesCapturadasManual.length >= MAX_PRODUCTOS_POR_CAJA
                        }
                      />
                    </div>

                    {mostrarDropdownClaveManual &&
                      lineaManual &&
                      clavesCapturadasManual.length < MAX_PRODUCTOS_POR_CAJA &&
                      sugerenciasClaveManual.length > 0 && (
                        <div className="ec-autocomplete-dropdown">
                          {sugerenciasClaveManual.map((prod) => {
                            const medidaTxt = construirMedidaEtiqueta(
                              prod,
                              "interior_exterior"
                            );
                            const metaPartes = [
                              prod.familia,
                              prod.marca,
                              prod.perfil ? `Perfil ${prod.perfil}` : "",
                            ].filter(Boolean);

                            return (
                              <div
                                key={prod.clave}
                                className="ec-autocomplete-item ec-autocomplete-item-col"
                                onClick={() => agregarClaveACajaManual(prod)}
                              >
                                <div className="d-flex justify-content-between align-items-center w-100">
                                  <span className="fw-semibold text-dark">
                                    {prod.clave}
                                  </span>
                                  {medidaTxt !== "-" && (
                                    <span className="ec-medida-pill">{medidaTxt}</span>
                                  )}
                                </div>
                                <div className="ec-autocomplete-sub">
                                  {metaPartes.length > 0
                                    ? metaPartes.join(" · ")
                                    : prod.descripcion}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>

                  {/* LISTA DE CLAVES CAPTURADAS EN ORDEN MANUAL (SUBIR / BAJAR / ELIMINAR) */}
                  <div className="ec-manual-keys-box">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="ec-helper-text fw-medium text-secondary">
                        Orden de impresión en la caja:
                      </span>
                      {clavesCapturadasManual.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger text-decoration-none p-0"
                          style={{ fontSize: "0.72rem" }}
                          onClick={() => setClavesCapturadasManual([])}
                        >
                          Vaciar claves
                        </button>
                      )}
                    </div>

                    {clavesCapturadasManual.length === 0 ? (
                      <div className="ec-manual-empty-keys">
                        Aún no hay claves en esta caja. Busque y seleccione hasta 6 claves arriba.
                      </div>
                    ) : (
                      <div className="ec-manual-keys-list">
                        {clavesCapturadasManual.map((prod, idx) => {
                          const prodEnriq =
                            mapaProductosPorClave.get(
                              String(prod.clave || "").trim().toUpperCase()
                            ) || prod;
                          const medidaTxt = construirMedidaEtiqueta(
                            prodEnriq,
                            "interior_exterior"
                          );
                          const metaTxt = [
                            prodEnriq.familia,
                            prodEnriq.marca,
                            prodEnriq.perfil,
                          ]
                            .filter(Boolean)
                            .join(" · ");

                          return (
                            <div key={prod.clave} className="ec-manual-key-item">
                              <span className="ec-manual-key-idx">{idx + 1}</span>
                              <div className="ec-manual-key-info">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="fw-semibold text-dark">
                                    {prod.clave}
                                  </span>
                                  {medidaTxt !== "-" && (
                                    <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                                      ({medidaTxt})
                                    </span>
                                  )}
                                </div>
                                {metaTxt && (
                                  <div className="ec-manual-key-meta">{metaTxt}</div>
                                )}
                              </div>
                              <div className="ec-manual-key-actions">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary ec-btn-icon-xs"
                                  disabled={idx === 0}
                                  onClick={() => moverClaveEnCajaManual(idx, -1)}
                                  title="Subir posición"
                                >
                                  <BiChevronUp />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary ec-btn-icon-xs"
                                  disabled={idx === clavesCapturadasManual.length - 1}
                                  onClick={() => moverClaveEnCajaManual(idx, 1)}
                                  title="Bajar posición"
                                >
                                  <BiChevronDown />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger ec-btn-icon-xs"
                                  onClick={() => eliminarClaveDeCajaManual(prod.clave)}
                                  title="Quitar clave"
                                >
                                  <BiTrash />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* 3. CAPTURA DEL NÚMERO DE CAJA Y BOTÓN PARA AGREGAR A LA HOJA */}
                  <div className="ec-manual-box-footer">
                    <div className="row g-2 align-items-end">
                      <div className="col-4">
                        <label className="form-label ec-field-label mb-1">
                          3. Caja #
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm ec-control-sm text-center fw-semibold"
                          placeholder="Ej. 1"
                          value={numeroCajaManual}
                          onChange={(e) => setNumeroCajaManual(e.target.value)}
                        />
                      </div>
                      <div className="col-8">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ec-control-sm w-100 d-flex align-items-center justify-content-center gap-1"
                          onClick={guardarCajaManualEnHoja}
                          disabled={!lineaManual || clavesCapturadasManual.length === 0}
                        >
                          <BiPlus />
                          {idCajaManualEditando
                            ? `Guardar cambios (Caja ${numeroCajaManual})`
                            : "Guardar caja y armar otra"}
                        </button>
                      </div>
                    </div>
                    {idCajaManualEditando && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-secondary text-decoration-none p-0 mt-1 w-100"
                        style={{ fontSize: "0.74rem" }}
                        onClick={cancelarEdicionCajaManual}
                      >
                        Cancelar edición
                      </button>
                    )}
                  </div>

                  {/* COLA DE CAJAS GUARDADAS EN LA HOJA (PARA OPTIMIZAR PAPEL) */}
                  {cajasManualesGuardadas.length > 0 && (
                    <div className="ec-manual-saved-section">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="ec-field-label mb-0">
                          Cajas guardadas en hoja ({cajasManualesGuardadas.length})
                        </span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger text-decoration-none p-0"
                          style={{ fontSize: "0.72rem" }}
                          onClick={() => {
                            setCajasManualesGuardadas([]);
                            setIdCajaManualEditando(null);
                          }}
                        >
                          Limpiar todas
                        </button>
                      </div>
                      <div className="ec-manual-saved-list">
                        {cajasManualesGuardadas.map((c) => {
                          const activaEd = c.id === idCajaManualEditando;
                          return (
                            <div
                              key={c.id}
                              className={`ec-manual-saved-item ${
                                activaEd ? "active" : ""
                              }`}
                            >
                              <div className="ec-manual-saved-meta">
                                <span className="badge bg-dark me-1">
                                  Caja {c.numeroCaja}
                                </span>
                                <span className="fw-semibold">{c.linea}</span>
                                <span className="text-muted ms-1">
                                  ({c.productos.length}{" "}
                                  {c.productos.length === 1 ? "clave" : "claves"})
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary ec-btn-icon-xs"
                                  onClick={() => editarCajaManualGuardada(c)}
                                  title="Editar esta caja"
                                >
                                  <BiEditAlt />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger ec-btn-icon-xs"
                                  onClick={() => eliminarCajaManualGuardada(c.id)}
                                  title="Eliminar esta caja de la hoja"
                                >
                                  <BiTrash />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
            <div className="ec-card h-100">
              <div className="ec-card-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <span className="ec-step-badge">Paso 3</span>
                  <span className="ec-card-title">
                    <BiSliderAlt className="me-1" />
                    Capacidad por Caja
                  </span>
                </div>
                {reglasCapacidad.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none p-0"
                    onClick={reiniciarReglas}
                    title="Restablecer a 4 productos por caja en toda la línea"
                  >
                    <BiRefresh /> Resetear
                  </button>
                )}
              </div>

              <div className="ec-card-body">
                {/* Resumen rápido de métricas */}
                <div className="ec-metrics-strip mb-3">
                  <div className="ec-metric-pill">
                    <span className="ec-metric-num">{productosOrdenados.length}</span>
                    <span className="ec-metric-lbl">Productos</span>
                  </div>
                  <div className="ec-metric-pill">
                    <span className="ec-metric-num">{cajasGeneradas.length}</span>
                    <span className="ec-metric-lbl">Cajas</span>
                  </div>
                  <div className="ec-metric-pill">
                    <span className="ec-metric-num">{hojasCarta.length}</span>
                    <span className="ec-metric-lbl">Hojas Carta</span>
                  </div>
                </div>

                <p className="text-muted small mb-2" style={{ fontSize: "0.76rem" }}>
                  Defina cuántos productos caben a partir de cada caja. También puede hacer
                  clic en el botón <strong>⚙️</strong> de cualquier etiqueta en la vista previa.
                </p>

                {/* Tabla / Lista de Tramos en Cascada */}
                <div className="ec-tramos-list mb-3">
                  {resumenTramos.map((tramo, index) => {
                    const esBase = tramo.desdeCaja === 1;
                    return (
                      <div
                        key={tramo.id}
                        className={`ec-tramo-item ${!tramo.activa ? "ec-tramo-inactivo" : ""}`}
                      >
                        <div className="ec-tramo-top">
                          <span className="ec-tramo-tag">Tramo #{index + 1}</span>
                          {tramo.activa ? (
                            <span className="ec-tramo-range">
                              Cajas <strong>{tramo.desdeCaja}</strong> a{" "}
                              <strong>{tramo.hastaCaja}</strong> ({tramo.totalProdsTramo} prods)
                            </span>
                          ) : (
                            <span className="badge bg-warning text-dark" style={{ fontSize: "0.68rem" }}>
                              Fuera de rango (máx caja {cajasGeneradas.length})
                            </span>
                          )}
                          {!esBase && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger ec-btn-del-tramo"
                              onClick={() => eliminarRegla(tramo.id)}
                              title="Eliminar este corte"
                            >
                              <BiTrash />
                            </button>
                          )}
                        </div>

                        <div className="ec-tramo-controls mt-2">
                          <div className="d-flex align-items-center gap-1">
                            <span className="small text-secondary">Desde caja:</span>
                            {esBase ? (
                              <span className="badge bg-secondary">Caja 1</span>
                            ) : (
                              <input
                                type="number"
                                min={2}
                                max={Math.max(2, cajasGeneradas.length)}
                                className="form-control form-control-sm ec-input-num"
                                value={tramo.desdeCaja}
                                onChange={(e) =>
                                  cambiarDesdeCajaEnRegla(tramo.id, e.target.value)
                                }
                              />
                            )}
                          </div>

                          <div className="d-flex align-items-center gap-1">
                            <span className="small text-secondary">Prods/caja:</span>
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                disabled={tramo.productosPorCaja <= 1}
                                onClick={() =>
                                  definirCapacidadDesdeCaja(
                                    tramo.desdeCaja,
                                    Math.max(1, tramo.productosPorCaja - 1)
                                  )
                                }
                              >
                                -
                              </button>
                              <span className="btn btn-light fw-bold px-2 disabled text-dark border-secondary">
                                {tramo.productosPorCaja}
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                disabled={tramo.productosPorCaja >= MAX_PRODUCTOS_POR_CAJA}
                                onClick={() =>
                                  definirCapacidadDesdeCaja(
                                    tramo.desdeCaja,
                                    Math.min(
                                      MAX_PRODUCTOS_POR_CAJA,
                                      tramo.productosPorCaja + 1
                                    )
                                  )
                                }
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Formulario para insertar un nuevo punto de corte */}
                <form onSubmit={handleAgregarNuevaReglaManual} className="ec-add-rule-box">
                  <div className="ec-field-label mb-2 text-primary">
                    <BiPlus className="me-1" />
                    Agregar cambio a partir de otra caja
                  </div>
                  <div className="row g-2 align-items-end">
                    <div className="col-5">
                      <label className="form-label small mb-1" style={{ fontSize: "0.72rem" }}>
                        A partir de Caja #
                      </label>
                      <input
                        type="number"
                        min={2}
                        max={Math.max(2, cajasGeneradas.length)}
                        className="form-control form-control-sm ec-control-sm"
                        placeholder={`Ej. ${Math.min(22, Math.max(2, cajasGeneradas.length))}`}
                        value={nuevaReglaCaja}
                        onChange={(e) => setNuevaReglaCaja(e.target.value)}
                      />
                    </div>
                    <div className="col-4">
                      <label className="form-label small mb-1" style={{ fontSize: "0.72rem" }}>
                        Prods / Caja
                      </label>
                      <select
                        className="form-select form-select-sm ec-control-sm"
                        value={nuevaReglaCantidad}
                        onChange={(e) => setNuevaReglaCantidad(Number(e.target.value))}
                      >
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <option key={n} value={n}>
                            {n} {n === 1 ? "prod" : "prods"}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-3">
                      <button type="submit" className="btn btn-primary btn-sm ec-control-sm w-100">
                        Aplicar
                      </button>
                    </div>
                  </div>
                </form>

                {/* Opciones adicionales de impresión */}
                <hr className="my-3" />
                <div className="ec-print-options">
                  <label className="form-label ec-field-label mb-1">
                    Rango de cajas a imprimir:
                  </label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="ec-helper-text text-muted">De caja</span>
                    <input
                      type="number"
                      min={1}
                      max={cajasGeneradas.length}
                      className="form-control form-control-sm ec-input-num ec-control-sm"
                      value={cajaInicioPrint}
                      onChange={(e) => setCajaInicioPrint(e.target.value)}
                    />
                    <span className="ec-helper-text text-muted">a</span>
                    <input
                      type="number"
                      min={1}
                      max={cajasGeneradas.length}
                      className="form-control form-control-sm ec-input-num ec-control-sm"
                      value={cajaFinPrint}
                      onChange={(e) => setCajaFinPrint(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="form-label ec-field-label mb-1">
                      Sección en encabezado (Opcional, sobreescribe auto):
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm ec-control-sm"
                      placeholder="Auto según productos de la caja"
                      value={seccionManualHeader}
                      onChange={(e) => setSeccionManualHeader(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* COLUMNA DERECHA: PREVISUALIZACIÓN EN VIVO DE HOJAS TAMAÑO CARTA (2x2) */}
          <div className="ec-preview-panel">
            <div className="ec-preview-toolbar">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="ec-card-title">
                  Vista Previa Tamaño Carta (4 etiquetas por hoja)
                </span>
                {!cargandoVistaPrevia && (
                  <span className="text-muted ec-helper-text">
                    Mostrando {cajasActivasVistaPrevia.length}{" "}
                    {cajasActivasVistaPrevia.length === 1 ? "caja" : "cajas"} en{" "}
                    {hojasActivasVistaPrevia.length}{" "}
                    {hojasActivasVistaPrevia.length === 1 ? "hoja" : "hojas"}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary ec-control-sm d-flex align-items-center gap-1"
                onClick={handlePrint}
                disabled={cargandoVistaPrevia || cajasActivasVistaPrevia.length === 0}
              >
                <BiPrinter /> Imprimir ahora
              </button>
            </div>

            <div className="ec-sheets-scroll">
              {cargandoVistaPrevia ? (
                /* SKELETON DE HOJA CARTA 2x2 MIENTRAS RESPONDE /inventario/productos-etiquetas-caja */
                <div className="ec-letter-sheet ec-skeleton-sheet">
                  <div className="ec-skeleton-banner">
                    <span
                      className="spinner-border spinner-border-sm text-primary me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    <span>
                      Preparando etiquetas de la línea{" "}
                      <strong>
                        {modoGeneracion === "manual" ? lineaManual : lineaSeleccionada}
                      </strong>{" "}
                      (obteniendo medidas, familia, perfil y existencias)...
                    </span>
                  </div>
                  <div className="ec-sheet-grid-2x2">
                    {[1, 2, 3, 4].map((numSkeleton) => (
                      <div key={`skel-box-${numSkeleton}`} className="ec-box-label ec-skeleton-card">
                        {/* Header Skeleton */}
                        <div className="ec-label-header">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="ec-skel-line ec-skel-w45 ec-skel-h18" />
                            <div className="ec-skel-line ec-skel-w30 ec-skel-h14" />
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="ec-skel-line ec-skel-w60 ec-skel-h12" />
                            <div className="ec-skel-badge" />
                          </div>
                        </div>

                        {/* Body Skeleton (4 filas de productos simuladas) */}
                        <div className="ec-label-body">
                          {[1, 2, 3, 4].map((rowIdx) => (
                            <div key={`skel-row-${rowIdx}`} className="ec-product-row">
                              <div className="ec-skel-barcode" />
                              <div className="ec-info-col">
                                <div className="ec-skel-line ec-skel-w85 ec-skel-h14 mb-1" />
                                <div className="ec-skel-line ec-skel-w60 ec-skel-h12" />
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Footer Skeleton */}
                        <div className="ec-label-footer">
                          <div className="ec-skel-logo" />
                          <div className="flex-grow-1">
                            <div className="ec-skel-line ec-skel-w70 ec-skel-h10 mb-1" />
                            <div className="ec-skel-line ec-skel-w50 ec-skel-h10" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : cajasActivasVistaPrevia.length === 0 ? (
                <div className="ec-empty-state my-auto">
                  <BiLayer size={34} className="text-secondary mb-2" />
                  <div className="ec-empty-title">
                    Sin etiquetas de caja en la hoja actual
                  </div>
                  <p className="ec-helper-text text-muted mb-0">
                    Busque una línea en el panel izquierdo, seleccione hasta 6 claves y capture el
                    número de caja para previsualizar e imprimir hasta 4 etiquetas por hoja tamaño carta.
                  </p>
                </div>
              ) : (
                /* CONTENEDOR IMPRIMIBLE REAL */
                <div ref={printRef} className="ec-print-root">
                {hojasActivasVistaPrevia.map((cajasEnHoja, hojaIdx) => (
                  <div key={`hoja-${hojaIdx}`} className="ec-letter-sheet">
                    {/* Indicador de número de hoja (solo en pantalla) */}
                    <div className="ec-sheet-badge no-print">
                      Hoja {hojaIdx + 1} de {hojasActivasVistaPrevia.length} (Cajas{" "}
                      {cajasEnHoja[0]?.numeroCaja} -{" "}
                      {cajasEnHoja[cajasEnHoja.length - 1]?.numeroCaja})
                    </div>

                    <div className="ec-sheet-grid-2x2">
                      {cajasEnHoja.map((caja, cajaIdx) => {
                        const textoSeccionHeader = caja.esManual
                          ? caja.seccionAuto
                          : seccionManualHeader.trim() !== ""
                          ? seccionManualHeader.trim()
                          : incluirSeccion
                          ? caja.seccionAuto
                          : "";

                        return (
                          <div
                            key={caja.id || `caja-${caja.numeroCaja}-${cajaIdx}`}
                            className={`ec-box-label ${
                              caja.esInicioDeTramo ? "ec-box-tramo-start" : ""
                            } ${caja.enCaptura || caja.enEdicion ? "ec-box-live-draft" : ""}`}
                          >
                            {/* Controles flotantes solo en pantalla */}
                            {!caja.esManual ? (
                              <div className="ec-inline-rule-trigger no-print">
                                {caja.esInicioDeTramo && (
                                  <span className="ec-tramo-pill">
                                    {caja.capacidadAplicada} prod/caja
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="ec-gear-btn"
                                  title={`Cambiar cantidad de productos a partir de la caja ${caja.numeroCaja}`}
                                  onClick={() =>
                                    setCajaEditandoInline(
                                      cajaEditandoInline === caja.numeroCaja
                                        ? null
                                        : caja.numeroCaja
                                    )
                                  }
                                >
                                  ⚙️ Capacidad
                                </button>

                                {cajaEditandoInline === caja.numeroCaja && (
                                  <div className="ec-inline-popover">
                                    <div className="ec-popover-title">
                                      A partir de <strong>caja {caja.numeroCaja}</strong> guardar:
                                    </div>
                                    <div className="ec-popover-btns">
                                      {[1, 2, 3, 4, 5, 6].map((cant) => (
                                        <button
                                          key={cant}
                                          type="button"
                                          className={`btn btn-sm ${
                                            caja.capacidadAplicada === cant
                                              ? "btn-primary"
                                              : "btn-outline-secondary"
                                          }`}
                                          onClick={() =>
                                            definirCapacidadDesdeCaja(
                                              caja.numeroCaja,
                                              cant
                                            )
                                          }
                                        >
                                          {cant}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="ec-inline-rule-trigger no-print">
                                {caja.enCaptura ? (
                                  <span className="ec-tramo-pill">En captura</span>
                                ) : caja.enEdicion ? (
                                  <span className="ec-tramo-pill">Editando</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="ec-gear-btn"
                                    onClick={() => editarCajaManualGuardada(caja)}
                                    title="Editar las claves o número de esta caja"
                                  >
                                    ✎ Editar
                                  </button>
                                )}
                              </div>
                            )}

                            {/* ENCABEZADO DE LA ETIQUETA */}
                            <div className="ec-label-header">
                              <div className="ec-header-row-1">
                                <div className="ec-linea-block">
                                  <span className="ec-lbl">LINEA:</span>{" "}
                                  <span className="ec-linea-val">
                                    {caja.linea || lineaSeleccionada}
                                  </span>
                                </div>
                                <div className="ec-seccion-block">
                                  <span className="ec-lbl">SECCION:</span>{" "}
                                  <span className="ec-seccion-val">
                                    {textoSeccionHeader}
                                  </span>
                                </div>
                              </div>

                              <div className="ec-header-row-2">
                                <div className="ec-familia-block">
                                  <span className="ec-lbl">FAMILIA:</span>{" "}
                                  <span className="ec-familia-val">
                                    {caja.familiaTexto || familiaTexto}
                                  </span>
                                </div>
                                <div className="ec-caja-num">
                                  caja {caja.numeroCaja}
                                </div>
                              </div>
                            </div>

                            {/* CUERPO: LISTA DE PRODUCTOS DE LA CAJA */}
                            <div className="ec-label-body">
                              {caja.productos.map((prod) => (
                                <div
                                  key={prod.clave}
                                  className="ec-product-row"
                                >
                                  <div className="ec-barcode-col">
                                    <BoxBarcode
                                      value={prod.clave}
                                      width={BARCODE_FIXED_WIDTH}
                                      height={BARCODE_FIXED_HEIGHT}
                                    />
                                  </div>

                                  <div className="ec-info-col">
                                    <div className="ec-prod-clave">
                                      {prod.clave}
                                    </div>
                                    <div className="ec-prod-medida">
                                      {construirMedidaEtiqueta(
                                        prod,
                                        caja.esManual
                                          ? "interior_exterior"
                                          : ordenDiametro
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* PIE DE LA ETIQUETA: LOGO + MARCA Y PERFIL SIN REPETICIÓN */}
                            <div className="ec-label-footer">
                              <img
                                src={Logo}
                                alt="Logo SM"
                                className="ec-footer-logo"
                              />
                              <div className="ec-footer-meta">
                                <div className="ec-footer-line">
                                  <strong>MARCA:</strong>{" "}
                                  <span>{caja.marcasTexto}</span>
                                </div>
                                <div className="ec-footer-line">
                                  <strong>PERFIL:</strong>{" "}
                                  <span>{caja.perfilesTexto}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtiquetasCajaTab;
